import type { ReactNode } from "react";

interface TableProps {
  children: ReactNode;
  caption?: string;
  className?: string;
}

export function Table({ children, caption, className = "" }: TableProps) {
  return (
    <div
      className={[
        "w-full overflow-x-auto rounded-[15px] border border-edlab-line bg-white",
        className,
      ].join(" ")}
    >
      <table className="min-w-full border-collapse text-sm whitespace-nowrap">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        {children}
      </table>
    </div>
  );
}

export function TableHead({ children }: { children: ReactNode }) {
  return (
    <thead className="bg-[#fcfdfd] text-left text-[11px] font-semibold tracking-[0.04em] text-[#89929d] uppercase">
      {children}
    </thead>
  );
}

export function TableBody({ children }: { children: ReactNode }) {
  return <tbody className="bg-white">{children}</tbody>;
}

export function TableRow({
  children,
  clickable,
}: {
  children: ReactNode;
  clickable?: boolean;
}) {
  return (
    <tr
      className={[
        clickable ? "cursor-pointer hover:[&>td]:bg-[#fafcfb]" : "hover:[&>td]:bg-[#fafcfb]/60",
      ].join(" ")}
    >
      {children}
    </tr>
  );
}

export function TableHeaderCell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`border-b border-edlab-line px-[18px] py-[15px] font-semibold ${className ?? ""}`.trim()}
    >
      {children}
    </th>
  );
}

export function TableCell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <td
      className={`border-b border-[#edf0f3] px-[18px] py-[17px] text-edlab-ink last:border-b-0 ${className ?? ""}`.trim()}
    >
      {children}
    </td>
  );
}

export function TableFooter({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "flex flex-wrap justify-between gap-3 border-t border-edlab-line px-[18px] py-3 text-xs text-[#8b939d]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}
